import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Book, Plus, FileText, Eye, Heart, SignOut, Pencil, BookmarkSimple, ShieldCheck, Gear, Crown, BookmarksSimple, BookOpen } from '@phosphor-icons/react';
import NotificationsDropdown from '../components/NotificationsDropdown';
import ThemeToggle from '../components/ThemeToggle';
import GlobalHeader from '../components/GlobalHeader';

export const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState({ draft: 0, pending: 0, published: 0, totalViews: 0, totalLikes: 0 });
  const [loading, setLoading] = useState(true);
  const [continueReading, setContinueReading] = useState([]);

  useEffect(() => {
    if (!user) return;

    // Fetch user's articles (may fail; must not block Continue Reading)
    const fetchArticles = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/articles?author_id=${user.user_id}`,
          { withCredentials: true }
        );
        const userArticles = response.data;
        setArticles(userArticles);
        setStats({
          draft: userArticles.filter(a => a.status === 'draft').length,
          pending: userArticles.filter(a => a.status === 'pending').length,
          published: userArticles.filter(a => a.status === 'published').length,
          totalViews: userArticles.reduce((sum, a) => sum + (a.views || 0), 0),
          totalLikes: userArticles.reduce((sum, a) => sum + (a.likes || 0), 0)
        });
      } catch (error) {
        console.error('Error fetching articles:', error);
      }
    };

    const fetchContinueReading = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/me/continue-reading`,
          { withCredentials: true }
        );
        setContinueReading(res.data || []);
      } catch (e) {
        console.error('Failed to fetch continue reading:', e);
      }
    };

    Promise.allSettled([fetchArticles(), fetchContinueReading()]).finally(() => setLoading(false));
  }, [user]);

  const dismissReading = async (articleId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/me/continue-reading/${articleId}`,
        { withCredentials: true }
      );
      setContinueReading(continueReading.filter(r => r.article_id !== articleId));
    } catch (e) {
      console.error('Failed to dismiss reading:', e);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <div className="mb-12">
          <h2 className="text-4xl md:text-5xl tracking-tight mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
            Welcome back, {user?.name}
          </h2>
          <p className="text-muted-foreground text-lg">Here is an overview of your writing journey</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-12">
          <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-draft">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Drafts</p>
              <FileText size={24} weight="duotone" className="text-muted-foreground" />
            </div>
            <p className="text-3xl font-bold">{stats.draft}</p>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-pending">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Pending</p>
              <FileText size={24} weight="duotone" className="text-amber-500" />
            </div>
            <p className="text-3xl font-bold">{stats.pending}</p>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-published">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Published</p>
              <FileText size={24} weight="duotone" className="text-green-600" />
            </div>
            <p className="text-3xl font-bold">{stats.published}</p>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-views">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Total Views</p>
              <Eye size={24} weight="duotone" className="text-primary" />
            </div>
            <p className="text-3xl font-bold">{stats.totalViews}</p>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-likes">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Total Likes</p>
              <Heart size={24} weight="duotone" className="text-red-500" />
            </div>
            <p className="text-3xl font-bold">{stats.totalLikes}</p>
          </div>
        </div>

        {/* Continue Reading */}
        {continueReading.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-8 mb-8" data-testid="continue-reading-section">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl tracking-tight flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                <BookOpen size={24} weight="duotone" className="text-primary" />
                Continue Reading
              </h3>
              <Link to="/collections" data-testid="my-collections-link" className="text-sm text-primary hover:underline">
                My Reading Lists →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {continueReading.map((r) => (
                <div key={r.article_id} data-testid={`continue-${r.article_id}`} className="border border-border rounded-lg p-4 hover:border-primary transition-colors relative">
                  <button
                    onClick={() => dismissReading(r.article_id)}
                    data-testid={`dismiss-continue-${r.article_id}`}
                    className="absolute top-2 right-2 text-muted-foreground hover:text-foreground text-xs w-6 h-6 rounded-full flex items-center justify-center hover:bg-muted"
                    title="Remove from list"
                  >
                    ×
                  </button>
                  <Link to={`/article/${r.article_id}`} className="block">
                    <h4 className="font-semibold text-base mb-1 pr-6 line-clamp-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {r.article_title}
                    </h4>
                    {r.author_name && <p className="text-xs text-muted-foreground mb-3">by {r.author_name}</p>}
                    <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mb-1">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${Math.round((r.progress || 0) * 100)}%` }}
                        data-testid={`progress-bar-${r.article_id}`}
                      ></div>
                    </div>
                    <p className="text-xs text-muted-foreground">{Math.round((r.progress || 0) * 100)}% read</p>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-card border border-border rounded-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
              Your Articles
            </h3>
          </div>
          {articles.length === 0 ? (
            <div className="text-center py-16" data-testid="no-articles-message">
              <Pencil size={48} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-6">You have not written any articles yet</p>
              <Link
                to="/editor/new"
                data-testid="start-writing-button"
                className="inline-block bg-primary text-primary-foreground rounded-full py-3 px-8 font-semibold transition-transform hover:scale-[1.02] hover:opacity-90"
              >
                Start Writing
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {articles.map((article) => (
                <div
                  key={article.article_id}
                  data-testid={`article-${article.article_id}`}
                  className="border border-border rounded-lg p-6 hover:border-primary transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full ${
                          article.status === 'published' ? 'bg-green-100 text-green-700' :
                          article.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {article.status}
                        </span>
                        <span className="text-xs text-muted-foreground">{article.category}</span>
                      </div>
                      
                      <h4 className="text-xl tracking-tight mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}>
                        {article.title}
                      </h4>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye size={16} />
                          {article.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart size={16} />
                          {article.likes}
                        </span>
                        <span>{new Date(article.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <Link
                      to={`/editor/${article.article_id}`}
                      data-testid={`edit-article-${article.article_id}`}
                      className="text-primary hover:underline text-sm font-medium"
                    >
                      Edit
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
